import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import { useState } from "react";
import { ExportFormat, ExportOptions } from "@/types/export.types";
import { validatePassword } from "@/utils/crypto.utils";

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  onExport: (options: ExportOptions) => Promise<void>;
}

export default function ExportDialog({
  open,
  onClose,
  onExport,
}: ExportDialogProps): JSX.Element {
  const [format, setFormat] = useState<ExportFormat>("custom");
  const [encrypted, setEncrypted] = useState(false);
  const [compressed, setCompressed] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const handlePasswordChange = (value: string): void => {
    setPassword(value);
    setPasswordError("");
  };

  const handleConfirmPasswordChange = (value: string): void => {
    setConfirmPassword(value);
    setPasswordError("");
  };

  const validateForm = (): boolean => {
    if (encrypted) {
      if (!validatePassword(password)) {
        setPasswordError("密码长度至少 6 位");
        return false;
      }
      if (password !== confirmPassword) {
        setPasswordError("两次输入的密码不一致");
        return false;
      }
    }
    return true;
  };

  const handleExport = async (): Promise<void> => {
    setError("");

    if (!validateForm()) {
      return;
    }

    setExporting(true);

    try {
      const options: ExportOptions = {
        format,
        encrypted,
        password: encrypted ? password : undefined,
        compressed,
      };

      console.log("[ExportDialog] 开始导出，选项:", options);
      await onExport(options);
      console.log("[ExportDialog] 导出成功");
      handleClose();
    } catch (err) {
      console.error("[ExportDialog] 导出失败:", err);
      setError(err instanceof Error ? err.message : "导出失败");
    } finally {
      setExporting(false);
    }
  };

  const handleClose = (): void => {
    setFormat("custom");
    setEncrypted(false);
    setCompressed(true);
    setPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>导出地址</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            导出格式
          </Typography>
          <RadioGroup
            value={format}
            onChange={(e) => setFormat(e.target.value as ExportFormat)}
          >
            <FormControlLabel value="csv" control={<Radio />} label="CSV 格式" />
            <FormControlLabel
              value="custom"
              control={<Radio />}
              label="自定义格式 (.qaddr)"
            />
          </RadioGroup>
        </FormControl>

        {format === "custom" && (
          <>
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={encrypted}
                    onChange={(e) => setEncrypted(e.target.checked)}
                  />
                }
                label="启用加密"
              />
            </Box>

            {encrypted && (
              <Box sx={{ mb: 3, ml: 4 }}>
                <TextField
                  fullWidth
                  label="密码"
                  type="password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  error={!!passwordError}
                  helperText={passwordError}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="确认密码"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                />
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={compressed}
                    onChange={(e) => setCompressed(e.target.checked)}
                  />
                }
                label="启用压缩 (推荐)"
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={exporting}>
          取消
        </Button>
        <Button
          onClick={() => void handleExport()}
          variant="contained"
          disabled={exporting}
        >
          {exporting ? "导出中..." : "导出"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
